import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { MatPaginator } from "@angular/material/paginator";
import { ioDealTypeMapping } from "src/app/models/insertion-order";

@Component({
  selector: "app-items-for-bulk-edit",
  templateUrl: "./items-for-bulk-edit.component.html",
  styleUrls: ["./items-for-bulk-edit.component.scss"],
})
export class ItemsForBulkEditComponent implements OnInit {
  @Input() selectedItems: Array<any> = [];
  @Input() selectedFields: Array<any> = [];
  @Input() entityType: string = "";
  @Input() minEndDate: string = "";
  @Input() maxStartDate: string = "";
  @Input() step: string = "";
  @Input() clientCurrency: string = "";
  @Input() clientTimeZone: string = "";
  @Input() dspType: string = "";
  @Input() isBulkEdit: boolean = false;

  dataSource: MatTableDataSource<any>;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  selectedItemColumn: string[] = [];
  ioDealTypeMapping = ioDealTypeMapping;

  biddingStratergy: Object = {
    cpm_paced: "CPM Paced",
    cpa_paced: "CPA Paced",
    cpc_paced: "CPC Paced",
  };

  daysOfWeekOrder = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  selectedHours: Array<{ [day: string]: number[] }> = [];

  constructor() {}

  ngOnInit(): void {
    this.setSelectedItemColumn();
  }

  updateTable() {
    this.dataSource = new MatTableDataSource(this.selectedItems);
    this.dataSource.paginator = this.paginator;
    this.dataSource._updatePaginator(this.selectedItems.length);
  }

  isValidNumber(value): boolean {
    return !isNaN(value) && value != null;
  }

  getSelectedHours = (selectedItems: Array<any>) => {
    const result: Array<{ [day: string]: number[] }> = [];

    const timeSlots = selectedItems.map(
      (ele: Object) => ele["targeting"]["time"]["user_time_of_week"]
    );

    timeSlots.forEach((slots) => {
      const hoursByDay: Record<string, Set<number>> = {};

      slots.forEach(({ day, start_hour, end_hour }) => {
        if (!hoursByDay[day]) {
          hoursByDay[day] = new Set<number>();
        }

        for (let hour = start_hour; hour < end_hour; hour++) {
          hoursByDay[day].add(hour);
        }
      });

      const dayHours: { [day: string]: number[] } = {};

      for (const day of this.daysOfWeekOrder) {
        dayHours[day] = hoursByDay[day]
          ? Array.from(hoursByDay[day]).sort((a, b) => a - b)
          : [];
      }

      result.push(dayHours);
    });
    return result;
  };

  setSelectedItemColumn() {
    if (this.entityType == "insertion_order") {
      if (this.dspType == "apnx" && this.step == "all") {
        this.selectedItemColumn = [
          "index",
          "name",
          "id",
          "budget_type",
          "type",
          "io_deal_type",
          "budget_intervals",
        ];
      } else if (
        this.dspType == "bdsp" &&
        (this.step == "all" || this.step == "basic")
      ) {
        this.selectedItemColumn = [
          "index",
          "name",
          "id",
          "budget.type",
          "budget.total_budget",
          "start_date",
          "end_date",
        ];
      } else if (this.dspType == "tdsp") {
        if (this.step == "updateTdspIoFlights") {
          this.selectedItemColumn = ["index", "name", "id", "flights"];
        } else {
          this.selectedItemColumn = ["index", "name", "id", "pixels"];
        }
      }
      this.updateTable();
    } else if (this.entityType == "line_item") {
      if (this.step == "all") {
        this.selectedItemColumn = [
          "index",
          "name",
          "id",
          "start_date",
          "end_date",
          "bidding.pacing",
          "targeting.geo.country_action",
          "targeting.geo.zip_action",
        ];
      } else if (this.step == "basic") {
        this.selectedItemColumn = [
          "index",
          "name",
          "id",
          "start_date",
          "end_date",
          "budget.lifetime",
          "budget.daily",
          "active_flights",
          "bidding.stratergy",
          "frequency",
          "use_fallback",
        ];
      } else if (this.step == "events") {
        this.selectedItemColumn = ["name", "id", "events"];
      } else if (this.step == "creatives") {
        this.selectedItemColumn = ["name", "id", "creatives"];
      } else if (this.step == "geo") {
        this.selectedItemColumn = [
          "index",
          "name",
          "id",
          "targeting.geo.country",
          "targeting.geo.region",
          "targeting.geo.city",
          "targeting.geo.zip",
          "targeting.geo.country_action",
          "targeting.geo.region_action",
          "targeting.geo.city_action",
          "targeting.geo.zip_action",
        ];
      } else if (this.step == "inventory") {
        this.selectedItemColumn = ["index", "name", "id"];
        const actionFields = [];
        if (this.selectedFields.includes("device")) {
          this.selectedItemColumn.push("targeting.platform.device_type");
        }
        if (this.selectedFields.includes("inventory_type")) {
          this.selectedItemColumn.push(
            "targeting.environment.environment_type"
          );
        }
        if (this.selectedFields.includes("domains")) {
          this.selectedItemColumn.push("targeting.app_site.domains");
        }
        if (this.selectedFields.includes("content_category")) {
          this.selectedItemColumn.push("targeting.content.content_category");
        }
        if (this.selectedFields.includes("app_bundle")) {
          this.selectedItemColumn.push("targeting.app_site.app_bundle");
        }
        if (this.selectedFields.includes("operating_system")) {
          actionFields.push("targeting.platform.operating_system_action");
          this.selectedItemColumn.push("targeting.platform.operating_system");
        }
        if (this.selectedFields.includes("ads_txt")) {
          actionFields.push("targeting.environment.ads_txt_action");
          this.selectedItemColumn.push("targeting.environment.ads_txt");
        }
        if (this.selectedFields.includes("native_layout")) {
          actionFields.push("targeting.environment.native_layout_action");
          this.selectedItemColumn.push("targeting.environment.native_layout");
        }
        this.selectedItemColumn.push(...actionFields);
      } else if (this.step == "segment") {
        this.selectedItemColumn = ["name", "id", "targeting.user.segment"];
        if (this.selectedFields.includes("user_id_present")) {
          this.selectedItemColumn.push("user_id_present");
        }
        if (this.selectedFields.includes("segments")) {
          this.selectedItemColumn.push("segment");
        }
      } else if (this.step == "day_part") {
        this.selectedItemColumn = [
          "name",
          "id",
          "targeting.time.user_time_of_week",
        ];
        this.selectedHours = this.getSelectedHours(this.selectedItems);
      } else if (this.step == "audio") {
        const actionFields = [];
        if (this.selectedFields.includes("audio_start_delay")) {
          actionFields.push("targeting.audio.audio_start_delay_action");
          this.selectedItemColumn.push("targeting.audio.audio_start_delay");
        }
        this.selectedItemColumn.push(...actionFields);
      } else if (this.step == "video") {
        const actionFields = [];
        if (this.selectedFields.includes("start_delay")) {
          actionFields.push("targeting.video.start_delay_action");
          this.selectedItemColumn.push("targeting.video.start_delay");
        }
        if (this.selectedFields.includes("player_size")) {
          actionFields.push("targeting.video.player_size_action");
          this.selectedItemColumn.push("targeting.video.player_size");
        }
        if (this.selectedFields.includes("video_placement_type")) {
          actionFields.push("targeting.video.video_placement_type_action");
          this.selectedItemColumn.push("targeting.video.video_placement_type");
        }
        if (this.selectedFields.includes("playback_method")) {
          actionFields.push("targeting.video.playback_method_action");
          this.selectedItemColumn.push("targeting.video.playback_method");
        }
        this.selectedItemColumn.push(...actionFields);
      }

      this.updateTable();
    }
  }
}
